import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPost, adminPatch, adminDelete } from "lib/adminClient";

/**
 * Admission rounds — the school's own control over each admission session.
 *
 * Before this existed the session ("2025–26"), the open classes and the fees
 * were hard-coded in the React bundle, so every new year needed a developer and
 * a rebuild. The public Admissions page now reads whichever round is active.
 *
 * Exactly one round per institution can be active; switching one on turns the
 * others off (enforced in the API and by a partial unique index).
 */

const INSTITUTIONS = [
  ["kcns", "KCNS — Nursery / Primary"],
  ["kcgs", "KCGS — School & College"],
  ["honors", "KCCWC — Honours"],
];

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green bg-white";
const btnPrimary =
  "bg-green text-white border-none rounded-lg px-3.5 py-2 font-sans font-semibold text-[12px] cursor-pointer hover:opacity-90 disabled:opacity-50";
const btnGhost =
  "bg-white text-ink border-[1.5px] border-line rounded-lg px-3 py-1.5 font-sans font-semibold text-[12px] cursor-pointer hover:border-green";
const btnDanger = "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]";

/** "2026-01-15T00:00:00.000Z" -> "2026-01-15" for <input type="date">. */
const toDateInput = (v) => {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  return new Date(new Date(v).getTime() + 6 * 60 * 60 * 1000).toISOString().slice(0, 10);
};

const blankRound = (institution = "kcgs") => ({
  institution,
  academicYear: "",
  label: "",
  opensAt: "",
  closesAt: "",
  classes: [],
  active: false,
  acceptingApplications: false,
  closedNote: "",
});

function Field({ label, hint, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[12px] font-semibold text-ink">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-muted leading-snug">{hint}</span>}
    </label>
  );
}

/** The classes this round accepts, each with its optional group list and fee. */
function ClassRows({ rows, onChange }) {
  const list = rows || [];
  const upd = (i, k, v) => onChange(list.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  return (
    <div className="flex flex-col gap-2.5">
      {list.length === 0 && (
        <div className="text-muted text-[13px]">
          No classes yet — applicants will have nothing to choose from.
        </div>
      )}
      {list.map((row, i) => (
        <div key={i} className="border border-line rounded-xl p-3 bg-[#fcfcfb] flex gap-3 items-start">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_1.6fr_1fr] gap-2.5">
            <Field label="Class">
              <input
                value={row.class || ""}
                onChange={(e) => upd(i, "class", e.target.value)}
                placeholder="Class VI"
                className={inputCls}
              />
            </Field>
            <Field label="Groups" hint="Comma separated. Leave blank if the class has no group choice.">
              <input
                value={(row.groups || []).join(", ")}
                onChange={(e) =>
                  upd(
                    i,
                    "groups",
                    e.target.value.split(",").map((g) => g.trim()).filter(Boolean)
                  )
                }
                placeholder="Science, Humanities, Commerce"
                className={inputCls}
              />
            </Field>
            <Field label="Fee" hint="Shown to the applicant.">
              <input
                value={row.fee || ""}
                onChange={(e) => upd(i, "fee", e.target.value)}
                placeholder="Tk. 500"
                className={inputCls}
              />
            </Field>
          </div>
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            title="Remove class"
            className={`${btnDanger} mt-5`}
          >
            🗑
          </button>
        </div>
      ))}
      <div>
        <button
          type="button"
          onClick={() => onChange([...list, { class: "", groups: [], fee: "" }])}
          className={btnGhost}
        >
          + Add class
        </button>
      </div>
    </div>
  );
}

function RoundEditor({ round, onChange, onSave, onRemove, saving }) {
  const set = (k, v) => onChange({ ...round, [k]: v });
  const isNew = !round.id;
  const scheduled = round.opensAt && new Date(round.opensAt) > new Date();
  const expired = round.closesAt && new Date(round.closesAt) < new Date();
  const availability = !round.active ? 'Not current' : round.acceptingApplications === false || expired ? 'Admissions closed' : scheduled ? 'Opening soon' : 'Admissions open';

  return (
    <div
      className="border rounded-xl overflow-hidden bg-white"
      style={{ borderColor: round.active ? "#005C40" : "#e2ddd6" }}
    >
      <div className="bg-[#f6f8f7] px-4 py-2.5 border-b border-line flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className="text-[12.5px] font-bold text-ink">
            {round.academicYear || "New round"}
          </span>
          {round.active && (
            <span className="text-[10.5px] font-bold uppercase tracking-wider rounded px-2 py-0.5 bg-green-light text-green-dark">
              {availability}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onSave} disabled={saving} className={btnPrimary}>
            {saving ? "Saving…" : isNew ? "Create round" : "Save"}
          </button>
          {!isNew && (
            <button type="button" onClick={onRemove} title="Delete round" className={btnDanger}>
              🗑
            </button>
          )}
        </div>
      </div>

      <div className="p-4 flex flex-col gap-3.5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Institution">
            <select
              value={round.institution}
              onChange={(e) => set("institution", e.target.value)}
              className={inputCls}
            >
              {INSTITUTIONS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </Field>
          <Field label="Session / Academic year" hint="Set separately for each institution, e.g. 2026 or 2026–27. Create a new round for a new session.">
            <input
              value={round.academicYear || ""}
              onChange={(e) => set("academicYear", e.target.value)}
              placeholder="2026–27"
              className={inputCls}
            />
          </Field>
          <Field label="Heading (optional)" hint="Overrides “Admissions <year>” on the page.">
            <input
              value={round.label || ""}
              onChange={(e) => set("label", e.target.value)}
              placeholder="ভর্তি ২০২৬–২৭"
              className={inputCls}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Applications open" hint="Leave blank for no published start date.">
            <input
              type="date"
              value={toDateInput(round.opensAt)}
              onChange={(e) => set("opensAt", e.target.value || null)}
              className={inputCls}
            />
          </Field>
          <Field label="Applications close">
            <input
              type="date"
              value={toDateInput(round.closesAt)}
              onChange={(e) => set("closesAt", e.target.value || null)}
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Classes open this round">
          <ClassRows rows={round.classes} onChange={(v) => set("classes", v)} />
        </Field>

        <Field
          label="Message when closed (optional)"
          hint="Shown when admissions are manually closed or the deadline has passed."
        >
          <textarea
            value={round.closedNote || ""}
            onChange={(e) => set("closedNote", e.target.value)}
            rows={2}
            placeholder="আবেদনের সময় শেষ হয়েছে। পরবর্তী বিজ্ঞপ্তির জন্য নোটিশ বোর্ড দেখুন।"
            className={`${inputCls} resize-y`}
          />
        </Field>

        <label className="flex items-start gap-2.5 cursor-pointer border border-line rounded-lg p-3">
          <input type="checkbox" checked={round.acceptingApplications !== false} onChange={e => set('acceptingApplications', e.target.checked)} className="mt-0.5" />
          <span><span className="block text-[13px] font-semibold text-ink">Accept new applications</span><span className="block text-[11.5px] text-muted mt-0.5">Turn off to close admissions immediately after saving. The session stays visible and existing applicants can still track their applications. Opening and closing dates also apply.</span></span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer border border-line rounded-lg p-3">
          <input
            type="checkbox"
            checked={!!round.active}
            onChange={(e) => set("active", e.target.checked)}
            className="mt-0.5 cursor-pointer"
          />
          <span>
            <span className="block text-[13px] font-semibold text-ink">
              Use this session on this institution’s admission page
            </span>
            <span className="block text-[11.5px] text-muted mt-0.5">
              Only one session is current per institution. Other institutions keep their own sessions and settings.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}

export default function AdmissionRoundsPage() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await adminGet("/admin/admission-rounds");
      setRounds(Array.isArray(rows) ? rows : []);
      setLoadFailed(false);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateAt = (i, next) => setRounds((arr) => arr.map((r, idx) => (idx === i ? next : r)));

  const save = async (i) => {
    const round = rounds[i];
    if (!(round.academicYear || "").trim()) {
      setError("Give the round an academic year before saving.");
      return;
    }
    setSavingId(round.id || `new-${i}`);
    setError("");
    setStatus("");
    try {
      // Blank date inputs must reach the API as null, not "".
      const body = {
        ...round,
        opensAt: round.opensAt ? `${toDateInput(round.opensAt)}T00:00:00+06:00` : null,
        closesAt: round.closesAt ? `${toDateInput(round.closesAt)}T23:59:59+06:00` : null,
      };
      delete body.id;
      delete body.createdAt;
      delete body.updatedAt;

      const saved = round.id
        ? await adminPatch(`/admin/admission-rounds/${round.id}`, body)
        : await adminPost("/admin/admission-rounds", body);

      // Activating one deactivates its siblings server-side, so reload rather
      // than patching a single row and showing two "Live" badges.
      if (saved.active) await load();
      else updateAt(i, saved);

      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (i) => {
    const round = rounds[i];
    if (!round.id) {
      setRounds((arr) => arr.filter((_, idx) => idx !== i));
      return;
    }
    if (!window.confirm(`Delete the ${round.academicYear} round for ${round.institution}?`)) return;
    try {
      await adminDelete(`/admin/admission-rounds/${round.id}`);
      setRounds((arr) => arr.filter((_, idx) => idx !== i));
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title={`Admission Settings (${rounds.length} sessions)`}
          action={
            <button
              onClick={() => setRounds([...rounds, blankRound()])}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Round
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-4">
          <div className="text-[12.5px] text-muted leading-relaxed">
            Configure each institution separately: its session, whether admissions are open,
            dates, classes and fees. Select the current session, choose whether to accept
            applications, then save. Closing admissions does not hide the session or existing applications.
          </div>
          {rounds.length === 0 && (
            <div className="text-muted text-sm">
              No rounds yet — click “Add Round” to open admissions for a session.
            </div>
          )}
          {rounds.map((round, i) => (
            <RoundEditor
              key={round.id || `new-${i}`}
              round={round}
              onChange={(next) => updateAt(i, next)}
              onSave={() => save(i)}
              onRemove={() => remove(i)}
              saving={savingId === (round.id || `new-${i}`)}
            />
          ))}
        </div>
      </AdminCard>

      {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
    </div>
  );
}
